
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import Snackbar from '@mui/material/Snackbar';
import { useState } from 'react';

import { type AccessoryOrderEntry } from '@/hooks/useAccessoryOrderItems';
import { type AccessoryOrderItem, useAccessoryOrderStore } from '@/stores/accessoryOrderStore';

import { AccessoryOrderSummary } from './AccessoryOrderSummary';
import { AccessoryOrderTable } from './AccessoryOrderTable';

/** The editable order list with its summary, and an undo prompt after a removal. */
export function AccessoryCartContent({ entries }: { entries: AccessoryOrderEntry[] }) {
  const items = useAccessoryOrderStore((s) => s.items);
  const remove = useAccessoryOrderStore((s) => s.remove);
  const clear = useAccessoryOrderStore((s) => s.clear);
  const setQuantity = useAccessoryOrderStore((s) => s.setQuantity);
  const restore = useAccessoryOrderStore((s) => s.restore);
  const [undo, setUndo] = useState<{ message: string; items: AccessoryOrderItem[] } | null>(null);

  const handleRemove = (slug: string) => {
    const item = items.find((i) => i.slug === slug);
    remove(slug);
    if (item) setUndo({ message: `${item.name} removed`, items: [item] });
  };
  const handleClear = () => {
    clear();
    setUndo({ message: 'Order list cleared', items });
  };

  return (
    <>
      <Grid container spacing={{ xs: 6, lg: 8 }} alignItems="flex-start">
        <Grid size={{ xs: 12, lg: 8 }}>
          <AccessoryOrderTable entries={entries} onRemove={handleRemove} onSetQuantity={setQuantity} />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <AccessoryOrderSummary entries={entries} onClear={handleClear} />
        </Grid>
      </Grid>
      <Snackbar
        open={undo !== null}
        autoHideDuration={6000}
        onClose={(_, reason) => reason !== 'clickaway' && setUndo(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          variant="filled"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                if (undo) restore(undo.items);
                setUndo(null);
              }}
            >
              Undo
            </Button>
          }
        >
          {undo?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
